import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import debounce from 'lodash.debounce';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { useTheme, useMediaQuery } from '@mui/material';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import {
    getCategoryOptionsForListFilter,
    normalizeProductForKitchenList,
} from '../../context-api/api-services/Products.service';
import { useKitchenProductsData } from '../../context-api/kitchen-products/KitchenProductsDataContext';
import { getManagingBranchNameForApi } from '../../utils/managingBranchName';
import { getProductsPageCache, setProductsPageCache } from '../../utils/productsPageCache';
import InputBox from '../../atomic/inputbox/InputBox';
import AtomicButton from '../../atomic/button/Button';
import KitchenListEditProductForm from '../../composed/products/kitchenListEditForm/KitchenListEditProductForm';
import KitchenListProductPreviewPanel from '../../composed/products/kitchenListEditForm/KitchenListProductPreviewPanel';
import {
    getStatusMenuProps,
    MenuProps,
    PRODUCT_SEARCH_DEBOUNCE_MS,
    sharedInputSx,
    statusOptions,
} from './kitchenProductsConstants';
import {
    formatLastRefresh,
    formatProductTypeLabel,
    getKitchenProductsLayoutStyles,
    normalizeApiStatusCounts,
    toTitleCase,
} from './kitchenProductsListHelpers';
import { KitchenProductsTable, ProductListStatCards } from './kitchenProductsListParts';

const KitchenProducts = () => {
    const navigate = useNavigate();
    const { setSelectedMenuTab } = useContext(GlobalStateDataContext);
    const {
        productsByManagingBranchData,
        fetchProductsByManagingBranch,
        markProductsByManagingBranchSatisfiedWithoutFetch,
        fetchProductCategory,
    } = useKitchenProductsData();
    const isLoading = productsByManagingBranchData.loading;
    const [searchParams] = useSearchParams();

    const kitchenNameParam = useMemo(() => {
        const raw = searchParams.get('kitchenName');
        return raw != null ? raw.trim() : '';
    }, [searchParams]);

    const [products, setProducts] = useState([]);
    const [apiStatusCounts, setApiStatusCounts] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedProductNameSearch, setDebouncedProductNameSearch] = useState('');
    const [networkError, setNetworkError] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [productTypes, setProductTypes] = useState(['None']);
    const [selectedProductType, setSelectedProductType] = useState('None');
    const [productCategories, setProductCategories] = useState(['None']);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [totalProducts, setTotalProducts] = useState(0);
    const [nonMealProducts, setNonMealProducts] = useState(0);
    const [mealProducts, setMealProducts] = useState(0);
    const [outOfStockProducts, setOutOfStockProducts] = useState(0);
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [categoryApiData, setCategoryApiData] = useState(null);
    const [categoriesApiError, setCategoriesApiError] = useState(null);
    const [lastRefreshAt, setLastRefreshAt] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);
    const forceNetworkRef = useRef(false);
    const listScopeKeyRef = useRef('');
    const skipSearchPageResetRef = useRef(true);
    const [viewportWidth, setViewportWidth] = useState(
        typeof window !== 'undefined' ? window.innerWidth : 1024
    );

    const theme = useTheme();
    const isNarrow = useMediaQuery(theme.breakpoints.down(900));
    const drawerContainerRef = useRef(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editProductClicked, setEditProductClicked] = useState(false);
    const [clickedProductDetail, setClickedProductDetail] = useState({});

    const showKitchenColumn = !!kitchenNameParam.length;

    const enrichProductForDrawer = useCallback((p) => {
        if (!p) return {};
        const kitchen =
            p.kitchen_name ??
            (typeof p.kitchen === 'string' && p.kitchen.trim() && p.kitchen !== '—' ? p.kitchen : '');
        return {
            ...p,
            kitchen_name: kitchen,
            price: p.price != null && p.price !== '' ? p.price : p.selling_price,
        };
    }, []);

    const openProductPreview = useCallback(
        (product) => {
            setClickedProductDetail(enrichProductForDrawer(product));
            setEditProductClicked(false);
            setIsDrawerOpen(true);
        },
        [enrichProductForDrawer]
    );

    const openProductEdit = useCallback(
        (product) => {
            setClickedProductDetail(enrichProductForDrawer(product));
            setEditProductClicked(true);
            setIsDrawerOpen(true);
        },
        [enrichProductForDrawer]
    );

    const deleteProductStub = useCallback(async (product) => {
        console.log('api hit', 'deleteProduct', {
            product_sku: product.product_sku,
            product_name: product.product_name,
        });
        return { success: true };
    }, []);

    const hydrateState = useCallback((rawList, apiTotal) => {
        const normalized = Array.isArray(rawList)
            ? rawList.map((item, i) => normalizeProductForKitchenList(item, i))
            : [];
        setProducts(normalized);
        const types = new Set(normalized.map((p) => p.product_type).filter(Boolean));
        types.add('non_meal_item');
        types.add('meal_item');
        setProductTypes(['None', ...Array.from(types).sort((a, b) => a.localeCompare(b))]);
        const total =
            typeof apiTotal === 'number' && !Number.isNaN(apiTotal) ? apiTotal : normalized.length;
        setTotalProducts(total);
        setNonMealProducts(normalized.filter((p) => p.product_type === 'non_meal_item').length);
        setMealProducts(normalized.filter((p) => p.product_type === 'meal_item').length);
        setOutOfStockProducts(normalized.filter((p) => (p.available_quantity || 0) <= 5).length);
    }, []);

    const commitDebouncedProductSearch = useMemo(
        () =>
            debounce((value) => {
                setDebouncedProductNameSearch(String(value ?? '').trim());
            }, PRODUCT_SEARCH_DEBOUNCE_MS),
        []
    );

    useEffect(() => {
        commitDebouncedProductSearch(searchQuery);
        return () => {
            commitDebouncedProductSearch.cancel();
        };
    }, [searchQuery, commitDebouncedProductSearch]);

    useEffect(() => {
        if (skipSearchPageResetRef.current) {
            skipSearchPageResetRef.current = false;
            return;
        }
        setCurrentPage(1);
    }, [debouncedProductNameSearch]);

    const sortedCategoriesForApi = useMemo(() => {
        if (!selectedCategories.length) return '';
        return [...selectedCategories]
            .map((c) => String(c).trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b))
            .join(',');
    }, [selectedCategories]);

    const listCacheFilters = useMemo(
        () => ({
            product_name_search: debouncedProductNameSearch,
            product_type: selectedProductType,
            categoryKey: sortedCategoriesForApi,
            status_type: selectedStatus,
        }),
        [debouncedProductNameSearch, selectedProductType, sortedCategoriesForApi, selectedStatus]
    );

    useEffect(() => {
        const branch = getManagingBranchNameForApi();
        if (!branch) {
            markProductsByManagingBranchSatisfiedWithoutFetch();
            setNetworkError(true);
            return undefined;
        }

        const listScopeKey = [
            branch,
            kitchenNameParam,
            listCacheFilters.product_name_search ?? '',
            listCacheFilters.product_type ?? '',
            listCacheFilters.categoryKey ?? '',
            listCacheFilters.status_type ?? '',
        ].join('\x1e');

        const forceNetwork = forceNetworkRef.current;
        forceNetworkRef.current = false;

        if (!forceNetwork) {
            const cached = getProductsPageCache(branch, kitchenNameParam, currentPage, itemsPerPage, listCacheFilters);
            if (cached) {
                markProductsByManagingBranchSatisfiedWithoutFetch();
                hydrateState(cached.data, cached.totalProducts);
                setApiStatusCounts(normalizeApiStatusCounts(cached.statusCounts));
                listScopeKeyRef.current = listScopeKey;
                setLastRefreshAt(cached.refreshedAt);
                setNetworkError(false);
                return undefined;
            }
        }

        let cancelled = false;
        setNetworkError(false);
        if (listScopeKeyRef.current !== listScopeKey) {
            setApiStatusCounts(null);
        }
        listScopeKeyRef.current = listScopeKey;
        setProducts([]);

        (async () => {
            try {
                const res = await fetchProductsByManagingBranch({
                    managing_branch_name: branch,
                    kitchen_name: kitchenNameParam || undefined,
                    page: currentPage,
                    limit: itemsPerPage,
                    product_type: selectedProductType,
                    product_category: sortedCategoriesForApi || undefined,
                    status_type: selectedStatus,
                    product_name_search: debouncedProductNameSearch || undefined,
                });
                if (cancelled) return;
                if (res.hasError) {
                    setNetworkError(true);
                    setApiStatusCounts(null);
                    hydrateState([], 0);
                    setLastRefreshAt(null);
                    return;
                }
                const payload = res.data || {};
                if (payload.success === false) {
                    setNetworkError(true);
                    setApiStatusCounts(null);
                    hydrateState([], 0);
                    setLastRefreshAt(null);
                    return;
                }
                const list = Array.isArray(payload.data) ? payload.data : [];
                const total =
                    typeof payload.total_products === 'number' ? payload.total_products : list.length;
                const refreshedAt = Date.now();
                const normalizedCounts = normalizeApiStatusCounts(payload.status_counts);
                setApiStatusCounts(normalizedCounts);
                hydrateState(list, total);
                setProductsPageCache(
                    branch,
                    kitchenNameParam,
                    currentPage,
                    itemsPerPage,
                    {
                        data: list,
                        totalProducts: total,
                        refreshedAt,
                        statusCounts: normalizedCounts,
                    },
                    listCacheFilters
                );
                setLastRefreshAt(refreshedAt);
            } catch (e) {
                if (cancelled) return;
                console.log('Error fetching products:', e);
                setNetworkError(true);
                setApiStatusCounts(null);
                hydrateState([], 0);
                setLastRefreshAt(null);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        kitchenNameParam,
        currentPage,
        itemsPerPage,
        reloadToken,
        hydrateState,
        listCacheFilters,
        fetchProductsByManagingBranch,
        markProductsByManagingBranchSatisfiedWithoutFetch,
    ]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const parsed = await fetchProductCategory({ forceRefresh: false });
            if (cancelled) return;
            if (parsed.ok) {
                setCategoryApiData(parsed.data);
                setCategoriesApiError(null);
            } else {
                setCategoryApiData(null);
                setCategoriesApiError(parsed.message || 'Could not load categories');
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [fetchProductCategory]);

    useEffect(() => {
        const fromApi = getCategoryOptionsForListFilter(categoryApiData, selectedProductType);
        const fromPage = products.map((p) => p.product_category).filter(Boolean);
        const merged = [...new Set([...fromApi, ...fromPage])].sort((a, b) => String(a).localeCompare(String(b)));
        setProductCategories(['None', ...merged]);
    }, [products, categoryApiData, selectedProductType]);

    useLayoutEffect(() => {
        setSearchQuery('');
        setDebouncedProductNameSearch('');
        skipSearchPageResetRef.current = true;
        setSelectedProductType('None');
        setSelectedCategories([]);
        setSelectedStatus('all');
        setCurrentPage(1);
    }, [kitchenNameParam]);

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const getProducts = useCallback(() => {
        forceNetworkRef.current = true;
        setReloadToken((t) => t + 1);
    }, []);

    const onDeleteProduct = useCallback(
        (product) => {
            Swal.fire({
                icon: 'warning',
                iconColor: '#e65100',
                title: 'Delete Product?',
                html: `
        <div style="font-size: 14px; color: #555; line-height: 1.6;">
          Do you want to permanently delete
          <b style="color: #1a1a2e;">${product.product_name || 'this product'}</b> ?
          <span style="color: #e53935; font-size: 12px; margin-top: 6px; display: inline-block;">
            ⚠ This action cannot be undone.
          </span>
        </div>
      `,
                showCancelButton: true,
                confirmButtonColor: '#e53935',
                cancelButtonColor: '#9e9e9e',
                confirmButtonText: 'Yes, Delete',
                cancelButtonText: 'Cancel',
                reverseButtons: true,
                focusCancel: true,
                showLoaderOnConfirm: true,
                allowOutsideClick: () => !Swal.isLoading(),
                preConfirm: async () => {
                    try {
                        await deleteProductStub(product);
                    } catch (error) {
                        const msg =
                            error?.message || 'Something went wrong while deleting the product. Please try again.';
                        Swal.showValidationMessage(msg);
                    }
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        html: `<b>${product.product_name || 'Product'}</b> has been deleted successfully.`,
                        confirmButtonColor: '#2e7d32',
                        confirmButtonText: 'OK',
                    }).then(() => {
                        getProducts();
                    });
                }
            });
        },
        [deleteProductStub, getProducts]
    );

    const handleSearchChange = useCallback((event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    }, []);

    const handleProductTypeChange = useCallback((selectedType) => {
        setSelectedProductType(selectedType);
        setSelectedCategories([]);
        setCurrentPage(1);
    }, []);

    const handleCategoryChange = useCallback((event) => {
        const {
            target: { value: rawValue },
        } = event;

        let value = typeof rawValue === 'string' ? rawValue.split(',') : rawValue;

        if (value.includes('None')) {
            value = [];
        }

        setSelectedCategories(value);
        setCurrentPage(1);
    }, []);

    const handleStatusChange = useCallback((event) => {
        setSelectedStatus(event.target.value);
        setCurrentPage(1);
    }, []);

    const totalPages = Math.max(1, Math.ceil(totalProducts / itemsPerPage) || 1);

    const handlePageChange = useCallback(
        (newPage) => {
            if (newPage >= 1 && newPage <= totalPages) {
                setCurrentPage(newPage);
            }
        },
        [totalPages]
    );

    const handleItemsPerPageChange = useCallback((e) => {
        const value = Number(e.target.value);
        if (!Number.isNaN(value) && value > 0) {
            setItemsPerPage(Math.min(100, Math.max(1, value)));
            setCurrentPage(1);
        }
    }, []);

    const isCompactLayout = viewportWidth <= 700;

    const {
        controlBarStyle,
        topRowStyle,
        filterGroupStyle,
        bottomRowStyle,
        headerRowStyle,
        lastRefreshInlineStyle,
    } = useMemo(() => getKitchenProductsLayoutStyles(isCompactLayout), [isCompactLayout]);

    const refreshControlBtnStyle = {
        padding: 0,
        border: 'none',
        background: 'none',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        color: '#000000ff',
        textDecoration: 'underline',
        fontSize: '12px',
        fontWeight: 600,
    };

    const lastRefreshActions = (
        <>
            <span>
                Last updated{' '}
                <span style={{ fontWeight: 600 }}>{formatLastRefresh(lastRefreshAt)}</span>
            </span>
            <span>·</span>
            <button type="button" onClick={getProducts} disabled={isLoading} style={refreshControlBtnStyle}>
                {isLoading ? 'Refreshing…' : 'Refresh'}
            </button>
        </>
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f1f5f9',
            }}
        >
            <div
                ref={drawerContainerRef}
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    padding: isCompactLayout ? '14px' : '24px',
                    maxWidth: '1080px',
                }}
            >
                {showKitchenColumn && (
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => {
                            setSelectedMenuTab('store-management');
                            navigate('/storemanagement');
                        }}
                        sx={{
                            mb: 2,
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            fontFamily: 'Inter Regular, sans-serif',
                            color: '#091057',
                        }}
                    >
                        Back to store management
                    </Button>
                )}

                <div style={headerRowStyle}>
                    <div
                        style={{
                            fontFamily: 'Inter Bold',
                            fontSize: isCompactLayout ? '24px' : '30px',
                            letterSpacing: '-0.02em',
                            color: '#1e293b',
                            lineHeight: 1.1,
                        }}
                    >
                        Products
                    </div>

                    <AtomicButton
                        label="ADD PRODUCT"
                        handleSubmit={() => {
                            const q = kitchenNameParam
                                ? `?${new URLSearchParams({ kitchenName: kitchenNameParam }).toString()}`
                                : '';
                            navigate(`/products/add${q}`);
                        }}
                        style={{
                            borderRadius: '5px',
                            width: isCompactLayout ? '100%' : '170px',
                            height: '42px',
                            fontWeight: '600',
                            letterSpacing: '0.3px',
                        }}
                    />
                </div>

                {kitchenNameParam.length > 0 && kitchenNameParam.length < 4 && (
                    <div
                        style={{
                            fontSize: '14px',
                            color: '#e65100',
                            marginBottom: '12px',
                        }}
                    >
                        Kitchen names must be at least 4 characters to filter by kitchen. Showing all products
                        for this branch.
                    </div>
                )}

                {kitchenNameParam.length >= 4 && (
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                        Filtered by kitchen:{' '}
                        <span style={{ fontWeight: 600, color: '#024CAA' }}>{kitchenNameParam}</span>
                    </div>
                )}

                {!kitchenNameParam.length && (
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '12px' }}>
                        All products for this branch. Open from{' '}
                        <RouterLink
                            to="/storemanagement"
                            onClick={() => setSelectedMenuTab('store-management')}
                            style={{ color: '#024CAA', fontWeight: 600 }}
                        >
                            Store management
                        </RouterLink>{' '}
                        and click a product count to filter by kitchen.
                    </div>
                )}

                {networkError && (
                    <div style={{ color: '#B91C1C', fontSize: '14px', marginBottom: '12px' }}>
                        Failed to load products. Check your connection and try Refresh.
                    </div>
                )}

                {categoriesApiError && (
                    <div style={{ color: '#b45309', fontSize: '13px', marginBottom: '10px' }}>
                        Could not load product categories ({categoriesApiError}). The category filter also includes
                        categories from the current page of products.
                    </div>
                )}

                <div style={controlBarStyle}>
                    <div style={topRowStyle}>
                        <InputBox
                            type="text"
                            label="Search by product name"
                            size="small"
                            height="auto"
                            sx={{
                                width: isCompactLayout ? '100%' : '260px',
                                ...sharedInputSx,
                            }}
                            name="search"
                            value={searchQuery}
                            onChange={handleSearchChange}
                        />

                        <div style={filterGroupStyle}>
                            <FormControl
                                size="small"
                                sx={{
                                    minWidth: isCompactLayout ? '100%' : '160px',
                                    ...sharedInputSx,
                                    '& .MuiSelect-select': {
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                <InputLabel id="product-type-select-label">Product type</InputLabel>
                                <Select
                                    labelId="product-type-select-label"
                                    id="product-type-select"
                                    value={selectedProductType}
                                    onChange={(e) => handleProductTypeChange(e.target.value)}
                                    input={<OutlinedInput label="Product type" />}
                                    MenuProps={MenuProps}
                                >
                                    {productTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {formatProductTypeLabel(type)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                size="small"
                                sx={{
                                    minWidth: isCompactLayout ? '100%' : '220px',
                                    ...sharedInputSx,
                                    '& .MuiSelect-select': {
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                <InputLabel id="category-select-label">Product Category</InputLabel>
                                <Select
                                    labelId="category-select-label"
                                    id="category-select"
                                    multiple
                                    value={selectedCategories}
                                    onChange={handleCategoryChange}
                                    input={<OutlinedInput label="Product Category" />}
                                    renderValue={(selected) => {
                                        if (!selected.length) {
                                            return 'None';
                                        }
                                        const maxChips = 2;
                                        const visible = selected.slice(0, maxChips);
                                        const rest = selected.length - maxChips;
                                        return (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    flexWrap: 'nowrap',
                                                    overflow: 'hidden',
                                                    minHeight: 24,
                                                }}
                                            >
                                                {visible.map((value) => (
                                                    <Chip
                                                        key={value}
                                                        label={toTitleCase(value)}
                                                        size="small"
                                                        sx={{ maxWidth: '100%' }}
                                                    />
                                                ))}
                                                {rest > 0 && (
                                                    <span style={{ fontSize: '12px', color: '#f97316', flexShrink: 0 }}>
                                                        +{rest} more
                                                    </span>
                                                )}
                                            </Box>
                                        );
                                    }}
                                    MenuProps={MenuProps}
                                    open={categoryOpen}
                                    onOpen={() => setCategoryOpen(true)}
                                    onClose={() => setCategoryOpen(false)}
                                >
                                    {productCategories.map((category) => (
                                        <MenuItem key={category} value={category}>
                                            {toTitleCase(category)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl
                                size="small"
                                sx={{
                                    width: isCompactLayout ? '100%' : '200px',
                                    minWidth: isCompactLayout ? '100%' : '200px',
                                    maxWidth: isCompactLayout ? '100%' : '200px',
                                    ...sharedInputSx,
                                    '& .MuiSelect-select': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                    },
                                }}
                            >
                                <InputLabel id="status-select-label">Status</InputLabel>
                                <Select
                                    labelId="status-select-label"
                                    id="status-select"
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                    label="Status"
                                    MenuProps={getStatusMenuProps(isCompactLayout)}
                                >
                                    {statusOptions.map((status) => (
                                        <MenuItem key={status.value} value={status.value}>
                                            {status.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>

                        {!isCompactLayout && <div style={lastRefreshInlineStyle}>{lastRefreshActions}</div>}
                    </div>

                    {isCompactLayout && (
                        <div style={bottomRowStyle}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    gap: '6px',
                                    fontSize: '12px',
                                    color: '#6f6f6fff',
                                    width: '100%',
                                }}
                            >
                                {lastRefreshActions}
                            </div>
                        </div>
                    )}
                </div>

                {isLoading && products.length === 0 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={40} sx={{ color: '#3F3F6E' }} />
                    </Box>
                ) : (
                    <>
                        <ProductListStatCards products={products} apiStatusCounts={apiStatusCounts} />

                        <KitchenProductsTable
                            filteredProducts={products}
                            rowNumberOffset={(currentPage - 1) * itemsPerPage}
                            showKitchenColumn={showKitchenColumn}
                            onProductRowClick={openProductPreview}
                            onEditProduct={openProductEdit}
                            onDeleteProduct={onDeleteProduct}
                        />

                        <SwipeableDrawer
                            anchor="right"
                            open={isDrawerOpen}
                            onClose={() => {
                                setIsDrawerOpen(false);
                                setEditProductClicked(false);
                            }}
                            onOpen={() => {
                                setIsDrawerOpen(true);
                            }}
                            swipeAreaWidth={56}
                            disableSwipeToOpen={false}
                            ModalProps={{ keepMounted: true }}
                            SwipeAreaProps={{ style: { zIndex: 10 } }}
                            container={drawerContainerRef.current}
                            sx={{
                                '& .MuiDrawer-paper': {
                                    width: isNarrow ? '100%' : 786,
                                    minWidth: isNarrow ? '100%' : 786,
                                },
                            }}
                        >
                            {editProductClicked && (
                                <KitchenListEditProductForm
                                    key={String(
                                        clickedProductDetail?.product_id ??
                                        clickedProductDetail?._id ??
                                        clickedProductDetail?.product_sku ??
                                        'edit'
                                    )}
                                    product={clickedProductDetail}
                                    onRefresh={getProducts}
                                    onClose={() => {
                                        setIsDrawerOpen(false);
                                        setEditProductClicked(false);
                                    }}
                                    onUpdate={getProducts}
                                />
                            )}
                            {!editProductClicked && (
                                <KitchenListProductPreviewPanel
                                    key={String(
                                        clickedProductDetail?.product_id ??
                                        clickedProductDetail?._id ??
                                        clickedProductDetail?.product_sku ??
                                        'preview'
                                    )}
                                    product={clickedProductDetail}
                                    onClose={() => {
                                        setIsDrawerOpen(false);
                                        setEditProductClicked(false);
                                    }}
                                    onEdit={() => setEditProductClicked(true)}
                                />
                            )}
                        </SwipeableDrawer>

                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '12px',
                                marginTop: '20px',
                            }}
                        >
                            <button
                                type="button"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                    padding: '6px 14px',
                                    border: '1px solid #d0d0d0',
                                    borderRadius: '4px',
                                    background: 'white',
                                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                    color: currentPage === 1 ? '#ccc' : '#333',
                                }}
                            >
                                Previous
                            </button>
                            <span style={{ fontSize: '13px', color: '#666' }}>
                                Page {currentPage} of {totalPages} &nbsp;·&nbsp; {products.length} on this page
                                &nbsp;·&nbsp; {totalProducts} total
                            </span>
                            <button
                                type="button"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{
                                    padding: '6px 14px',
                                    border: '1px solid #d0d0d0',
                                    borderRadius: '4px',
                                    background: 'white',
                                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                    color: currentPage === totalPages ? '#ccc' : '#333',
                                }}
                            >
                                Next
                            </button>
                            <span style={{ fontSize: '13px', color: '#999', marginLeft: '8px' }}>|</span>
                            <label
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    fontSize: '13px',
                                    color: '#666',
                                }}
                            >
                                Show
                                <select
                                    value={itemsPerPage}
                                    onChange={handleItemsPerPageChange}
                                    style={{
                                        padding: '6px 10px',
                                        border: '1px solid #d0d0d0',
                                        borderRadius: '4px',
                                        background: 'white',
                                        fontSize: '13px',
                                        color: '#333',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                                per page
                            </label>
                        </div>

                        <div
                            style={{
                                marginTop: '16px',
                                fontSize: '12px',
                                color: '#888',
                                textAlign: 'center',
                            }}
                        >
                            Total (API): {totalProducts} · This page — Regular: {nonMealProducts} · Combo:{' '}
                            {mealProducts} · Low stock (≤5): {outOfStockProducts}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default KitchenProducts;
